import { createTheme, ThemeProvider } from "@mui/material/styles";
import MUIDataTable from "mui-datatables";
import React, { useState, useEffect } from "react";

import { earliestYearMonth, getMonthLabelsInRange } from "../../utils/monthRange";
import LoadingOverlay from "../LoadingOverlay";

const CarrSummaryTable = ({
    props,
    onLoad,
    onDataStart,
    startMonth,
    endMonth,
    group,
    headerControl,
}) => {
    const [metricTableData, setMetricTableData] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    // Once the first load finishes, later loads are "Group By" refetches that show
    // an in-place progress bar over this table instead of the global overlay.
    const [hasLoadedOnce, setHasLoadedOnce] = useState(false);

    const fetchMetrics = () => {
        setIsLoading(true);
        props
            .fetchData("getcarrsummary", { group })
            .then((data) => {
                const metricsData = data || [];
                setMetricTableData(formatTableData(metricsData));
                if (onDataStart)
                    onDataStart(earliestYearMonth(metricsData.flatMap((row) => Object.keys(row))));
            })
            .catch((error) => console.error("Error fetching metrics:", error))
            .finally(() => {
                setIsLoading(false);
                setHasLoadedOnce(true);
                if (onLoad) onLoad();
            });
    };

    useEffect(() => {
        fetchMetrics();
    }, [group]);

    const getMuiTheme = () =>
        createTheme({
            components: {
                MuiTableCell: {
                    styleOverrides: {
                        root: {
                            border: "none", // Remove all cell borders
                        },
                    },
                },
            },
        });

    const formatTableData = (data) => {
        return data.map((item) => {
            const formattedItem = {
                ...item,
            };

            // Bold the Total row
            if (item.carr === "CARR" || item.carr === "Total CARR") {
                formattedItem.carr = <strong>{item.carr}</strong>;
            }

            if (item.carr == "% YoY Growth") {
                formattedItem.carr = <span style={{ paddingLeft: "10px" }}>{item.carr}</span>;
            }
            if (item.carr == "% of Total CARR") {
                formattedItem.carr = (
                    <div style={{ paddingLeft: "10px", paddingBottom: "15px" }}>{item.carr}</div>
                );
            }
            return formattedItem;
        });
    };

    const columns = [
        {
            name: "carr",
            label: " ",
            options: {
                setCellProps: () => ({
                    style: {
                        minWidth: "150px",
                        // width: "100%",
                        borderRight: "1px solid #D3D3D3",
                        fontSize: "12px",
                        padding: "0px",
                        paddingLeft: "14px",
                    },
                }),
                setCellHeaderProps: () => ({
                    style: {
                        // width: "100%",
                        minWidth: "fit-content",
                        fontWeight: "bold",
                        background: "#156082",
                        borderRight: "1px solid #D3D3D3",
                        padding: "0px",
                        paddingLeft: "14px",
                    },
                }),
            },
        },
        // Auto-generated month columns from the selected month range
        ...getMonthLabelsInRange(startMonth, endMonth).map((month, i, arr) => {
            const isEven = i % 2 === 0;
            const isLast = i === arr.length - 1;
            return {
                name: month,
                label: month,
                options: {
                    customBodyRender: (value) =>
                        value === null || value === undefined || value === "" ? "-" : value,
                    setCellProps: () => ({
                        style: {
                            minWidth: "fit-content",
                            fontSize: "12px",
                            textAlign: "right",
                            padding: "0px",
                            paddingLeft: "10px",
                            paddingRight: isLast ? "14px" : "0px", // Add right padding only for last
                            verticalAlign: "top",
                        },
                    }),
                    customHeadRender: ({ index, ...column }) => {
                        return (
                            <th
                                key={index}
                                className="text-right text-white"
                                style={{
                                    background: "#156082",
                                    fontSize: "12px",
                                    minWidth: "60px",
                                    paddingRight: isLast ? "14px" : "0px",
                                }}
                            >
                                {month}
                            </th>
                        );
                    },
                },
            };
        }),
    ];

    const options = {
        responsive: "standard",
        selectableRows: "none",
        searchAlwaysOpen: true,
        storageKey: "carrSummaryTable",
        filter: false,
        sort: false,
        viewColumns: false,
        print: false,
        download: false,
        search: false,
        columnOrder: null,
        fixedHeader: true,
        pagination: false,
        setRowProps: (row, dataIndex) => {
            const isFirst = dataIndex === 0;
            const isLast = dataIndex === metricTableData.length - 2;

            if (isLast) {
                return {
                    style: {
                        borderTop: "2px solid #000", // or borderTop for first
                    },
                };
            }

            if (isFirst) {
                return {
                    style: {
                        borderBottom: "2px solid #000", // or borderTop for first
                    },
                };
            }

            return {};
        },
    };

    const isReloading = isLoading && hasLoadedOnce;

    return (
        <div className="relative my-32">
            <div className="mb-4 flex items-center justify-between">
                <h2>CARR Summary</h2>
                {headerControl}
            </div>
            {isReloading && <LoadingOverlay contained text="Updating..." />}
            {isLoading && !hasLoadedOnce ? (
                onLoad ? null : (
                    <LoadingOverlay text="Loading metrics..." />
                )
            ) : metricTableData.length > 0 ? (
                <ThemeProvider theme={getMuiTheme()}>
                    <MUIDataTable
                        data={formatTableData(metricTableData)}
                        columns={columns}
                        options={options}
                        style={{ marginTop: "80px" }}
                        className="ngShadow"
                    />
                </ThemeProvider>
            ) : (
                <h3 className="px-2 pt-8 text-center">No metrics available.</h3>
            )}
        </div>
    );
};

export default CarrSummaryTable;
